// @flow
import React from 'react';
import Dialog from '@material-ui/core/Dialog';
import Rating from 'react-rating';
import Button from '@material-ui/core/Button';
import Star from '@material-ui/icons/Star';
import Close from '@material-ui/icons/Close';
import StarBorder from '@material-ui/icons/StarBorder';
import { IntercomAPI } from 'react-intercom';
import { withTranslation } from 'react-i18next';
import { createStyles, withStyles } from '@material-ui/core/styles';
import { withRouter } from 'react-router-dom';
import type { RouterHistory } from 'react-router-dom';

import {
  withMobileDialog,
  Typography,
  withTheme,
  Fab,
} from '@material-ui/core';
import { fetchWithToken, getAvaBackendEndpoint } from '../../utils/http';
import * as segment from '../../utils/segment';
import ParticipantAvatar from '../../components/ParticipantAvatar';
import { log } from '../../utils/log';
import { isMobile, getOS } from '../../utils';
import OpenOrDownloadAvaAppComponent from '../../components/OpenOrDownloadAvaAppComponent';

import Transition from '../../components/Transition';

const marginStyle = {
  marginLeft: 15,
  marginRight: 15,
};

const styles = createStyles(theme => ({
  paper: {
    backgroundColor: theme.palette.background[theme.palette.type],
    width: 500,
  },
}));

const fadeOutUpCustom = `
@keyframes fadeOutUpCustom {
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
    transform: translate3d(0, -50%, 0);
  }
}

.fadeOutUpCustom {
  animation-name: fadeOutUpCustom;
}
`;

const getColor = (rate?: number) => {
  if (!rate) {
    return '#150acef';
  }
  if (rate <= 2) {
    return '#DE423E';
  }
  if (rate !== 3) {
    return '#05bf6f';
  }
  return '#ED8822';
};

type Props = {
  classes: Object,
  download: Function,
  endedByCurrentUser: Boolean,
  endedDueToFreeLimit: Boolean,
  fullScreen: Boolean,
  getTranscriptStats: Function,
  restartConversation: Function,
  history: RouterHistory,
  scribe: boolean,
  speakers: Object,
  status: Object,
  t: Function,
  theme: Object,
  user: Object,
  dispatch: Function,
  inTwilioCalls: Boolean,
  conversationBroadcastedToZoom: Boolean,
};

type State = {
  rate?: number,
  display: boolean,
  transcriptSaved: boolean,
  ratingHover?: number,
  openOrDownloadAvaApp: boolean,
};

class WebConversationOverModal extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.submitRate = this.submitRate.bind(this);
    this.submit = this.submit.bind(this);
    this.trackHadConversationEvent = this.trackHadConversationEvent.bind(this);
    this.handleRateChange = this.handleRateChange.bind(this);
    this.state = {
      display: true,
      rate: undefined,
      transcriptSaved: false,
      ratingHover: undefined,
      openOrDownloadAvaApp: false,
    };
  }

  handleRateChange = (rate: number) => {
    const { rate: alreadyRate } = this.state;
    if (alreadyRate) return;
    this.setState({ rate });
    // Track as soon as we rate!
    this.trackHadConversationEvent(rate);
    // If no CTA, let's close
    const { user } = this.props;
    const isOnDesktopAndLoggedIn = window.isElectron && user.avaName;
    if ((rate >= 4 && isOnDesktopAndLoggedIn) || rate === 3) {
      setTimeout(this.submit, 2000);
    }
  };

  trackHadConversationEvent = async (rate: number) => {
    const {
      status,
      inTwilioCalls,
      conversationBroadcastedToZoom,
      user,
      endedByCurrentUser,
      endedDueToFreeLimit,
      getTranscriptStats,
      dispatch,
    } = this.props;
    const transcriptStats = getTranscriptStats();
    const { transcriptSaved } = this.state;
    // First we update convometrics in parse
    // with increments
    const isSoloConvo = status.speakers.length === 1;
    const currentMetrics = user.profile && user.profile.feedbackMetrics;
    // It's users first convo if he has no metrics
    const isFirstConvo =
      !currentMetrics ||
      (!currentMetrics.NB_SOLO_CONVO && !currentMetrics.NB_GROUP_CONVO);
    const durationSeconds = parseInt(
      (new Date().getTime() - status.startTimestampMs) / 1000,
      10
    );
    const thisConvoTotalWords =
      transcriptStats.wordsTypedGroup + transcriptStats.wordsCapturedGroup;
    const metricsToUpdate: any = {
      'Total Convo Time': durationSeconds,
      'Total Saved Transcripts': transcriptSaved ? 1 : 0,
      'Total Solo Time': isSoloConvo ? durationSeconds : 0,
      'Total Group Time': isSoloConvo ? 0 : durationSeconds,
      'Total Conversations': 1,
      'Total Convo Words': thisConvoTotalWords,
      'Total Solo Words': isSoloConvo ? thisConvoTotalWords : 0,
      'Total Group Words': isSoloConvo ? 0 : thisConvoTotalWords,
      'Total Solo Conversations Web': isSoloConvo ? 1 : 0,
      'Total Group Conversations Web': isSoloConvo ? 0 : 1,
    };
    if (isSoloConvo) {
      metricsToUpdate.NB_SOLO_CONVO = 1;
    } else {
      metricsToUpdate.NB_GROUP_CONVO = 1;
    }
    metricsToUpdate[`Rated_${rate || 0}`] = 1;
    await fetchWithToken(
      `${getAvaBackendEndpoint()}/api/v1.1/users/${
        user.avaId
      }?firebaseAuthUID=${user.firebaseAuthUID || user.id}`,
      {
        method: 'PATCH',
        body: JSON.stringify({
          convoMetrics: metricsToUpdate,
        }),
      }
    );
    let closeReason;
    if (endedDueToFreeLimit) {
      closeReason = 'max_convo_time_reached';
    } else if (
      endedByCurrentUser &&
      status.host &&
      status.host.avaId !== user.avaId
    ) {
      closeReason = 'closed';
    } else if (
      !endedByCurrentUser &&
      status.host &&
      status.host.avaId === user.avaId
    ) {
      closeReason = 'last_guest_left';
    } else {
      closeReason = 'host_left';
    }
    if (isFirstConvo) {
      segment.identifyWithId(user.avaId, { First_convo_at: new Date() });
    }
    segment.track('Had Conversation', {
      'Had Convo Date': new Date(),
      'Convo Channel': status.id,
      IsFirstConvo: isFirstConvo,
      IsHost: status.host && status.host.avaId === user.avaId,
      Scribe: !!status.scribe,
      IsTempUser: user.accountType === 'Temporary',
      IsScribeRequester:
        status.scribe && status.scribe.requestor === user.avaId,
      'Close Reason': closeReason,
      'Conversation Mode': status.conversationMode,
      'Participant Count': status.speakers.length,
      'Participant List': status.speakers.map(
        s => `${s.userName} / ${s.avaId} / ${s.avaName}`
      ),
      Rating: rate || 0,
      IsBroadcasted: conversationBroadcastedToZoom,
      IsDialIn: inTwilioCalls,
      IsFirst5Star: rate === 5 && !currentMetrics.Rated_5,
      TranscriptSaved: transcriptSaved,
      'Words Typed You': transcriptStats.wordsTypedYou,
      'Words Typed Group': transcriptStats.wordsTypedGroup,
      'Words Captured You': transcriptStats.wordsCapturedYou,
      'Words Captured Group': transcriptStats.wordsCapturedGroup,
    });
    // Now that update is done we need to fetch it again
    const updatedData = await fetchWithToken(
      `${getAvaBackendEndpoint()}/api/v1.1/users/me?firebaseAuthUID=${user.firebaseAuthUID ||
        user.id}`,
      {
        method: 'PATCH',
      }
    );
    const updatedProfile = await updatedData.json();
    const updatedMetrics =
      updatedProfile.user &&
      updatedProfile.user.convoMetrics;
    if (updatedMetrics) {
      dispatch({
        type: 'FETCH_AVA_PROFILE_METRICS',
        metrics:
          updatedProfile.user.profile &&
          updatedProfile.user.profile.feedbackMetrics,
      });
    }
  };

  submitRate = () => {
    const { status, user } = this.props;
    const { rate } = this.state;
    if (rate !== undefined) {
      fetchWithToken(
        `${getAvaBackendEndpoint()}/api/v1/conversation-rates?roomId=${
          status.id
        }&avaId=${user.avaId}&firebaseAuthUID=${user.id}`,
        {
          method: 'POST',
          json: true,
          body: JSON.stringify({ rate }),
        }
      );
    }
    log('hadConversation', 'info', {
      rate,
      status,
      user,
    });
  };

  componentWillReceiveProps(nextProps) {
    const { scribe, restartConversation } = nextProps;
    if (scribe) {
      this.setState({ display: false });
      restartConversation();
    }
  }

  submit = () => {
    const { restartConversation } = this.props;
    const { rate } = this.state;
    if (!rate) {
      // Had conversation is automatically
      // tracked when user rates, 
      // we need to call it when use closes popup
      this.trackHadConversationEvent(0);
    }
    this.submitRate();
    restartConversation();
  };

  openOrDownloadAvaApp() {
    // Since it's based on rendering a component,
    // we toggle the visibility alltogether
    this.setState({ openOrDownloadAvaApp: false });
    setTimeout(() => {
      this.setState({ openOrDownloadAvaApp: true });
    }, 0);
  }

  render() {
    const {
      classes,
      download,
      endedByCurrentUser,
      endedDueToFreeLimit,
      fullScreen,
      history,
      scribe,
      speakers,
      status,
      t,
      theme,
      user,
    } = this.props;
    const { display, ratingHover, openOrDownloadAvaApp } = this.state;
    if (!display) {
      return null;
    }
    const { rate } = this.state;
    const others = status.transcriptOwners.filter(id => id !== user.avaId);
    const hostId = status.id.split('_')[0];
    const host = speakers[hostId] || {};
    const hostName = host.userName || hostId;
    const othersSpeakers = others.map(id => speakers[id] || {});
    let othersString = othersSpeakers.map(s => s.userName).join(', ');
    if (othersString.length > 18) {
      othersString = `${othersString.slice(0, 15)}...`;
    }
    let title = '';
    if (endedDueToFreeLimit) {
      title = t('conversation.over.freeLimit');
    } else if (
      endedByCurrentUser &&
      status.host &&
      status.host.avaId !== user.avaId
    ) {
      title = t('conversation.over.exited');
    } else if (
      !endedByCurrentUser &&
      status.host &&
      status.host.avaId === user.avaId
    ) {
      title = t('conversation.over.everyoneHasLeft');
    } else {
      title = `${hostName} ${t('conversation.over.hasEndedTheConversation!')}`;
    }

    const mobile = isMobile();
    const OS = getOS();

    return (
      <Dialog
        open={!scribe}
        fullScreen={fullScreen}
        TransitionComponent={Transition}
        keepMounted
        onClose={() => {
          this.submit();
        }}
        aria-labelledby="form-dialog-title"
        style={{
          textAlign: 'center',
          margin: 'auto',
          backgroundColor: theme.palette.type === 'light' ? 'white' : undefined,
        }}
        classes={{
          paper: classes.paper,
        }}
      >
        <Fab
          key="download_convo_btn"
          onClick={() => this.submit()}
          variant="round"
          style={{
            backgroundColor: 'transparent',
            color: theme.palette.type === 'dark' ? 'white' : 'black',
            position: 'absolute',
            right: 15,
            top: 10,
            width: 36,
            height: 36,
          }}
        >
          <Close />
        </Fab>
        <div style={{ flexGrow: 0.5, marginTop: 20 }} />
        <div style={{ display: 'flex' }}>
          <div style={{ flexGrow: 1000 }} />
          <ParticipantAvatar nbOfConnection="1" user={host} size="big" />
          <div style={{ flexGrow: 1000 }} />
        </div>
        <Typography variant="h5" style={{ ...marginStyle }}>
          {title}
        </Typography>
        <div style={{ flexGrow: 0.3 }} />
        <div style={{ display: 'flex', flexDirection: 'column', height: 100 }}>
          {!!status.confidential && status.confidential.allowTranscriptsSaving && !!user.features && !user.features["forbid-conversation-saving"] && (
            <Button
              key="download_convo_btn"
              onClick={() => download()}
              variant="contained"
              style={{
                ...marginStyle,
                minWidth: 210,
                marginTop: 'auto',
                marginBottom: 'auto',
                fontWeight: 600,
              }}
            >
              <i className="material-icons">save_alt</i>
              <span style={{ textTransform: 'none', fontSize: 20 }}>
                {t('conversation.over.downloadTranscripts')}
              </span>
            </Button>
          )}
        </div>

        <div style={{ flexGrow: 1 }} />
        <style>{fadeOutUpCustom}</style>
        <div
          style={{
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            paddingBottom: 12,
          }}
          className={
            rate ? 'animate__animated animate__delay-1s fadeOutUpCustom' : ''
          }
        >
          <Typography variant="h5" style={{ ...marginStyle }}>
            {t('conversation.over.howDidAvaWorksForYou?')}
          </Typography>
          <div
            style={{
              maxWidth: 250,
              alignSelf: 'center',
            }}
          >
            <Rating
              emptySymbol={(
                <StarBorder
                  style={{
                    fontSize: 50,
                    color:
                      theme.palette.type === 'light'
                        ? theme.palette.ava.blue
                        : undefined,
                  }}
                />
              )}
              fullSymbol={(
                <Star
                  style={{
                    color: getColor(ratingHover || rate || 0),
                    fontSize: 50,
                  }}
                />
              )}
              readonly={!!rate}
              onChange={r => this.handleRateChange(r)}
              initialRating={rate}
              onHover={value => {
                if (ratingHover !== value) {
                  this.setState({ ratingHover: value });
                }
              }}
            />
            {rate === undefined && (
              <div style={{ display: 'flex', flexDirection: 'row' }}>
                <div
                  style={{
                    color: '#DE423E',
                    fontSize: 16,
                  }}
                >
                  {t('conversation.over.terrible')}
                </div>
                <div style={{ flexGrow: 1 }} />
                <div
                  style={{
                    color: '#4CCA6B',
                    fontSize: 16,
                  }}
                >
                  {t('conversation.over.perfect')}
                </div>
              </div>
            )}
          </div>
          <div style={{ minHeight: 32 }}>
            {rate && (
              <Typography
                className="animate__animated animate__delay-1s fadeOutUpCustom"
                variant="h6"
                style={{ color: getColor(rate), pointerEvents: 'none' }}
              >
                {rate !== undefined && (
                  <>{t(`conversation.over.rating.${rate.toString()}`)}</>
                )}
              </Typography>
            )}
          </div>
        </div>

        <div style={{ flexGrow: 0.2 }} />
        <div
          style={{
            height: 150,
            marginTop: -130,
          }}
        >
          {rate !== undefined &&
            user.avaName &&
            rate >= 4 &&
            !mobile &&
            !window.isElectron && (
              <div
                className="animate__animated animate__fadeInUp animate__delay-1s"
                style={{ display: 'flex', flexDirection: 'column' }}
              >
                <div style={{ marginBottom: 20 }}>
                  <Typography variant="h5" style={{ ...marginStyle }}>
                    {t('conversation.over.doYouWantSomeMore')}
                  </Typography>
                </div>
                <Button
                  key="download_convo_btn"
                  onClick={() => this.openOrDownloadAvaApp()}
                  variant="contained"
                  style={{
                    ...marginStyle,
                    flexGrow: 1,
                    minWidth: 210,
                    backgroundColor: theme.palette.ava.blue,
                    color: 'white',
                  }}
                >
                  <span
                    style={{
                      textTransform: 'none',
                      fontSize: 20,
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>
                      {t('conversation.over.openAva', { OS })}
                    </span>
                    <br />
                    <span
                      style={{
                        opacity: 0.8,
                        fontSize: 16,
                        display: 'block',
                        marginTop: -10,
                      }}
                    >
                      {t('conversation.over.forAMagicalExperience')}
                    </span>
                  </span>
                </Button>
              </div>
            )}
          {rate !== undefined && user.avaName && rate >= 4 && mobile && (
            <div
              className="animate__animated animate__fadeInUp animate__delay-1s"
              style={{ display: 'flex', flexDirection: 'column' }}
            >
              <div style={{ marginBottom: 20 }}>
                <Typography variant="h5" style={{ ...marginStyle }}>
                  {t('conversation.over.doYouWantSomeMore')}
                </Typography>
              </div>
              <Button
                key="download_convo_btn"
                onClick={() => this.openOrDownloadAvaApp()}
                variant="contained"
                style={{
                  ...marginStyle,
                  flexGrow: 1,
                  minWidth: 210,
                  backgroundColor: theme.palette.ava.blue,
                  color: 'white',
                }}
              >
                <span
                  style={{
                    textTransform: 'none',
                    fontSize: 20,
                  }}
                >
                  <span style={{ fontWeight: 600 }}>
                    {t('conversation.over.getAva')}
                  </span>
                  <br />
                  <span
                    style={{
                      opacity: 0.8,
                      fontSize: 16,
                      display: 'block',
                      marginTop: -10,
                    }}
                  >
                    {t('conversation.over.forAMagicalExperience')}
                  </span>
                </span>
              </Button>
            </div>
          )}
          {rate !== undefined && !user.avaName && rate >= 4 && mobile && (
            <div
              className="animate__animated animate__fadeInUp animate__delay-1s"
              style={{ display: 'flex', flexDirection: 'column' }}
            >
              <div style={{ marginBottom: 20 }}>
                <Typography variant="h5" style={{ ...marginStyle }}>
                  {t('conversation.over.planOnSpeakWith')} {hostName}{' '}
                  {t('conversation.over.again?')}
                </Typography>
              </div>
              <Button
                key="download_convo_btn"
                onClick={() => this.openOrDownloadAvaApp()}
                variant="contained"
                style={{
                  ...marginStyle,
                  flexGrow: 1,
                  minWidth: 210,
                  backgroundColor: theme.palette.ava.blue,
                  color: 'white',
                }}
              >
                <span
                  style={{
                    textTransform: 'none',
                    fontSize: 20,
                  }}
                >
                  <span style={{ fontWeight: 600 }}>
                    {t('conversation.over.downloadAvaApp')}
                  </span>
                  <br />
                  <span
                    style={{
                      opacity: 0.8,
                      fontSize: 16,
                      display: 'block',
                      marginTop: -10,
                    }}
                  >
                    {t('conversation.over.forAMagicalExperience')}
                  </span>
                </span>
              </Button>
            </div>
          )}
          {rate !== undefined && !user.avaName && rate >= 4 && !mobile && (
            <div
              className="animate__animated animate__fadeInUp animate__delay-1s"
              style={{ display: 'flex', flexDirection: 'column' }}
            >
              <div style={{ marginBottom: 20 }}>
                <Typography variant="h5" style={{ ...marginStyle }}>
                  {t('conversation.over.planOnSpeakWith')} {hostName}{' '}
                  {t('conversation.over.again?')}
                </Typography>
              </div>
              <Button
                onClick={() => {
                  this.submit();
                  setTimeout(() => history.push('/web/login'), 300);
                }}
                variant="contained"
                style={{
                  ...marginStyle,
                  flexGrow: 1,
                  minWidth: 210,
                  backgroundColor: theme.palette.ava.blue,
                  color: 'white',
                }}
              >
                <span
                  style={{
                    textTransform: 'none',
                    fontSize: 20,
                  }}
                >
                  <span style={{ fontWeight: 600 }}>
                    {t('conversation.over.transcribeYourOwnMeetings')}
                  </span>
                </span>
              </Button>
            </div>
          )}
          {rate !== undefined && !user.avaName && rate === 3 && (
            <div
              className="animate__animated animate__fadeInUp animate__delay-1s"
              style={{ display: 'flex', flexDirection: 'column' }}
            >
              <div style={{ marginBottom: 20 }}>
                <Typography variant="h5" style={{ ...marginStyle }}>
                  {t('conversation.over.planOnSpeakWith')} {hostName}{' '}
                  {t('conversation.over.again?')}
                </Typography>
              </div>
              <Button
                key="download_convo_btn"
                onClick={() => this.openOrDownloadAvaApp()}
                variant="contained"
                style={{
                  ...marginStyle,
                  flexGrow: 1,
                  minWidth: 210,
                  backgroundColor: theme.palette.ava.blue,
                  color: 'white',
                }}
              >
                <span
                  style={{
                    textTransform: 'none',
                    fontSize: 20,
                  }}
                >
                  <span style={{ fontWeight: 600 }}>
                    {t('conversation.over.downloadAvaApp')}
                  </span>
                  <br />
                  <span
                    style={{
                      opacity: 0.8,
                      fontSize: 16,
                      display: 'block',
                      marginTop: -10,
                    }}
                  >
                    {t('conversation.over.forAMagicalExperience')}
                  </span>
                </span>
              </Button>
            </div>
          )}
          {rate !== undefined && rate <= 2 && (
            <div
              className="animate__animated animate__fadeInUp animate__delay-1s"
              style={{ display: 'flex', flexDirection: 'column' }}
            >
              <div style={{ marginBottom: 20 }}>
                <Typography variant="h5" style={{ marginStyle }}>
                  {t('conversation.over.weAreSorryYouHadABadExperience')}
                </Typography>
              </div>
              <Button
                key="download_convo_btn"
                onClick={() => {
                  this.submit();
                  IntercomAPI(
                    'showNewMessage',
                    t('conversation.over.iHadTheFollowingIssues')
                  );
                }}
                variant="contained"
                style={{
                  ...marginStyle,
                  flexGrow: 1,
                  minWidth: 210,
                  backgroundColor: theme.palette.ava.blue,
                  color: 'white',
                  textTransform: 'none',
                  fontSize: 20,
                  fontWeight: 600,
                }}
              >
                {t('conversation.over.sendFeedback')}
              </Button>
            </div>
          )}
        </div>
        <div style={{ flexGrow: 1 }} />
        {openOrDownloadAvaApp && (
          <OpenOrDownloadAvaAppComponent />
        )}
      </Dialog>
    );
  }
}

export default withRouter(
  withStyles(styles)(
    withTranslation()(
      withMobileDialog({ breakpoint: 'xs' })(
        withTheme(WebConversationOverModal)
      )
    )
  )
);
